<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import type { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import { specialCurrenciesConfig } from '@/config/special-currencies'
import { ECOMMERCE_ITEM_BENEFIT_TYPE, type EcommerceItem } from '@/types/shared/player-config'
import { computed, ref } from 'vue'
import type { Nullable } from '@/types/shared/common'
import BaseButton from '@/components/base/BaseButton.vue'
import ShopItemDetails from '@/components/ShopItemDetails.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import ShopCard2 from '@/components/ShopCard2.vue'

const props = withDefaults(defineProps<{
	show: boolean,
	currency: SPECIAL_CURRENCY_ID
	ecommerceItems: EcommerceItem[]
	subtitle?: string
	hasCheckmark?: boolean
}>(), {
  subtitle: undefined,
  hasCheckmark: true,
})

const emit = defineEmits<{
	(e: 'hide', hideForToday: boolean): void
}>()

const selectedItem = ref<Nullable<EcommerceItem>>(null)
const hideForToday = ref(false)

const sortedEcommerceItems = computed(() => {
  return [...props.ecommerceItems].sort((a, b) => {
    return (a.prices?.[0]?.amount ?? 0) - (b.prices?.[0]?.amount ?? 0)
  })
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    :back-button="selectedItem !== null"
    bg-fade-class="!z-[804]"
    wrapper-class="!z-[804]"
    base-modal-class="overflow-visible"
    :content-class="`overflow-visible ${!selectedItem ? 'pt-[84px] pb-4' : ''}`"
    outer-content-class="overflow-visible"
    border-container-class="overflow-visible"
    @hide="$emit('hide', $event ? hideForToday : false)"
    @back="selectedItem = null"
  >
    <template
      v-if="!selectedItem"
      #baseContent
    >
      <img
        class="z-[1] absolute pointer-events-none -top-[100px] left-1/2 -translate-x-1/2 w-[236px]"
        :src="specialCurrenciesConfig[currency].image"
      >
    </template>
    <div class="flex w-full flex-col items-center relative">
      <template v-if="!selectedItem">
        <div
          class="whitespace-nowrap text-center text-[36px] leading-10 -tracking-[2px] font-bold golden-gradient-text"
        >
          Out of <span class="lowercase">{{ specialCurrenciesConfig[currency].label }}</span>?
        </div>
        <div
          class="mt-1 text-center text-base font-medium text-white/60"
        >
          {{ subtitle ?? 'Get more and keep exploring!' }}
        </div>
        <div
          class="relative mt-3 grid w-full grid-cols-3 gap-2"
        >
          <ShopCard2
            v-for="(item, index) in sortedEcommerceItems"
            :key="index"
            class="z-[2]"
            :item="item"
            :first-benefit-type="ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY"
            @click="selectedItem = item; hideForToday = false"
          />
        </div>
        <BaseButton
          class="mt-2 w-full"
          style-type="tertiary"
          @click="$emit('hide', hideForToday)"
        >
          no thanks
        </BaseButton>
        <div
          v-if="hasCheckmark"
          class="mt-2 flex h-[30px] w-full items-center justify-center  gap-x-1"
        >
          <BaseCheckbox v-model="hideForToday">
            <span class="text-xs font-semibold text-light-white/[.67]">Don’t show this again today.</span>
          </BaseCheckbox>
        </div>
      </template>
      <ShopItemDetails
        v-else
        :item="selectedItem"
        card-style="2"
        show-name
        card-outer-style="transform: scale(0.476); transform-origin: top;"
        :hide-after-purchase="false"
        ton-discount-badge-class="right-6"
        @hide="emit('hide', false)"
      />
    </div>
  </TheBottomSheet>
</template>

<style scoped>
.golden-gradient-text {
	background: linear-gradient(90deg,
	#FFF246 0%,
	#E5B80B 37%,
	#FFF571 69%,
	#E5B80B 100%
	);
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	background-clip: text;
	color: transparent;
}
</style>